import ClinicApi from '../api/clinic';

const env = process.env;
const clinicApi = new ClinicApi(
  env.VUE_APP_API_CLINIC_URL,
  env.VUE_APP_API_CLINIC_TIMEOUT
);

const state = {
  items: {},
  conclusions: {},
  diagnoses: [],
  diagnosisEstablishments: {},
  functionals: [],
  functionalEstablishments: {},
  anamneses: {},
  healthElements: [],
  healthState: {},
  healthContent: [],
  strategies: [],
  stages: {},
  drugPrescriptions: {},
};

const getters = {
  clientNotes: state => clientId => {
    if (!state.items[clientId]) {
      return [];
    }

    return state.items[clientId];
  },

  clientConclusions: state => clientId => {
    if (!state.conclusions[clientId]) {
      return [];
    }

    return state.conclusions[clientId];
  },

  clientDiagnosisEstablishments: state => clientId => {
    if (!state.diagnosisEstablishments[clientId]) {
      return [];
    }

    return state.diagnosisEstablishments[clientId];
  },

  clientFunctionalEstablishments: state => clientId => {
    if (!state.functionalEstablishments[clientId]) {
      return [];
    }

    return state.functionalEstablishments[clientId];
  },

  clientAnamneses: state => clientId => {
    if (!state.anamneses[clientId]) {
      return [];
    }

    return state.anamneses[clientId];
  },

  clientHealthState: state => clientId => {
    if (!state.healthState[clientId] || state.healthState[clientId].length === 0) {
      return [];
    }

    return state.healthState[clientId];
  },

  clientStages: state => clientId => {
    if (!state.stages[clientId]) {
      return [];
    }

    return state.stages[clientId];
  },

  clientClinicDrugPrescriptions: state => clientId => {
    if (!state.drugPrescriptions[clientId]) {
      return [];
    }

    return state.drugPrescriptions[clientId];
  },
};

const mutations = {
  NOTES_SET_ITEMS: (state, payload) => {
    state.items[payload.clientId] = payload.items;
    state.items = Object.assign({}, state.items);
  },

  SET_CONCLUSIONS: (state, payload) => {
    state.conclusions[payload.clientId] = payload.items;
    state.conclusions = Object.assign({}, state.conclusions);
  },

  SET_DIAGNOSES: (state, payload) => {
    state.diagnoses = payload;
  },

  SET_DIAGNOSIS_ESTABLISHMENTS: (state, payload) => {
    state.diagnosisEstablishments[payload.clientId] = payload.items;
    state.diagnosisEstablishments = Object.assign(
        {},
        state.diagnosisEstablishments
    );
  },

  SET_FUNCTIONALS: (state, payload) => {
    state.functionals = payload;
  },

  SET_FUNCTIONAL_ESTABLISHMENTS: (state, payload) => {
    state.functionalEstablishments[payload.clientId] = payload.items;
    state.functionalEstablishments = Object.assign(
        {},
        state.functionalEstablishments
    );
  },

  SET_HEALTH_ELEMENTS: (state, payload) => {
    state.healthElements = payload;
  },

  SET_HEALTH_STATE: (state, payload) => {
    state.healthState[payload.clientId] = payload.items;
    state.healthState = Object.assign({}, state.healthState);
  },

  SET_HEALTH_CONTENT: (state, payload) => {
    for (const content of payload) {
      if (typeof content.image !== 'undefined') {
        content.image = `${env.VUE_APP_API_CLINIC_URL}${content.image.url}`;
      }
    }

    state.healthContent = payload;
  },

  SET_ANAMNESES: (state, payload) => {
    state.anamneses[payload.clientId] = payload.items;
    state.anamneses = Object.assign({}, state.anamneses);
  },

  SET_STRATEGIES: (state, payload) => {
    state.strategies = payload.items.map(
        ({id, title, description}) => ({id, title, description})
    );
  },

  SET_STAGES: (state, payload) => {
    state.stages[payload.clientId] = payload.items;
    state.stages = Object.assign({}, state.stages);
  },

  CLINIC_SET_DRUG_PRESCRIPTIONS: (state, payload) => {
    state.drugPrescriptions[payload.clientId] = payload.items;
    state.drugPrescriptions = Object.assign({}, state.drugPrescriptions);
  },
};

const actions = {
  NOTES_LOAD_ITEMS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getNotes(token, payload);
    const data = { items: result.result.items, clientId: payload };

    context.commit('NOTES_SET_ITEMS', data);
  },

  NOTES_ADD_ITEM: async (context, payload) => {
    const token = context.rootState.token;
    const { client, text } = payload;

    await clinicApi.addNote(token, client, text);

    await context.dispatch('NOTES_LOAD_ITEMS', client);
  },

  NOTES_UPDATE_ITEM: async (context, payload) => {
    const token = context.rootState.token;
    const { id, text, client } = payload;

    await clinicApi.updateNote(token, id, text);

    await context.dispatch('NOTES_LOAD_ITEMS', client);
  },

  LOAD_CONCLUSIONS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getConclusions(token, payload);
    const data = { items: result.result, clientId: payload };

    context.commit('SET_CONCLUSIONS', data);
  },

  ADD_CONCLUSION: async (context, payload) => {
    const token = context.rootState.token;
    const { client, text, note, additional, dateFrom } = payload;

    await clinicApi.addConclusion(token, { user: client, text, note, additional, dateFrom });

    await context.dispatch('LOAD_CONCLUSIONS', client);
  },

  UPDATE_CONCLUSION: async (context, payload) => {
    const token = context.rootState.token;
    const { id, client, text, note, additional, dateFrom } = payload;

    await clinicApi.updateConclusion(token, id, { text, note, additional, dateFrom });

    await context.dispatch('LOAD_CONCLUSIONS', client);
  },

  DELETE_CONCLUSION: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user } = payload;

    await clinicApi.deleteConclusion(token, id);

    await context.dispatch('LOAD_CONCLUSIONS', user);
  },

  LOAD_DIAGNOSES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getDiagnoses(token);

    context.commit('SET_DIAGNOSES', result.result);
  },

  LOAD_DIAGNOSIS_ESTABLISHMENTS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getDiagnosisEstablishments(token, payload);
    const data = { items: result.result, clientId: payload };

    context.commit('SET_DIAGNOSIS_ESTABLISHMENTS', data);
  },

  ADD_DIAGNOSIS_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { client, diagnosis, note, dateFrom, dateTo, type } = payload;
    const data = { user: client, diagnosis, note, dateFrom, dateTo, type };

    await clinicApi.addDiagnosisEstablishment(token, data);

    await context.dispatch('LOAD_DIAGNOSIS_ESTABLISHMENTS', client);
  },

  UPDATE_DIAGNOSIS_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { id, client, note, dateFrom, dateTo, type } = payload;
    const data = { note, dateFrom, dateTo, type };

    await clinicApi.updateDiagnosisEstablishment(token, id, data);

    await context.dispatch('LOAD_DIAGNOSIS_ESTABLISHMENTS', client);
  },

  DELETE_DIAGNOSIS_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user } = payload;

    await clinicApi.deleteDiagnosisEstablishment(token, id);

    await context.dispatch('LOAD_DIAGNOSIS_ESTABLISHMENTS', user);
  },

  LOAD_FUNCTIONALS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getFunctionals(token);

    context.commit('SET_FUNCTIONALS', result.result);
  },

  LOAD_FUNCTIONAL_ESTABLISHMENTS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getFunctionalEstablishments(token, payload);
    const data = { items: result.result, clientId: payload };

    context.commit('SET_FUNCTIONAL_ESTABLISHMENTS', data);
  },

  ADD_FUNCTIONAL_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { client, functional, note, dateFrom, dateTo } = payload;
    const data = { user: client, functional, note, dateFrom, dateTo };

    await clinicApi.addFunctionalEstablishment(token, data);

    await context.dispatch('LOAD_FUNCTIONAL_ESTABLISHMENTS', client);
  },

  UPDATE_FUNCTIONAL_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { id, client, note, dateFrom, dateTo } = payload;
    const data = { note, dateFrom, dateTo };

    await clinicApi.updateFunctionalEstablishment(token, id, data);

    await context.dispatch('LOAD_FUNCTIONAL_ESTABLISHMENTS', client);
  },

  DELETE_FUNCTIONAL_ESTABLISHMENT: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user } = payload;

    await clinicApi.deleteFunctionalEstablishment(token, id);

    await context.dispatch('LOAD_FUNCTIONAL_ESTABLISHMENTS', user);
  },

  LOAD_HEALTH_ELEMENTS: async (context) => {
    const token = context.rootState.token;
    const data = await clinicApi.getHealthElements(token);

    context.commit('SET_HEALTH_ELEMENTS', data.result);
  },

  LOAD_HEALTH_STATE: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getHealthState(token, payload);
    const data = { items: result.result, clientId: payload };

    context.commit('SET_HEALTH_STATE', data);
  },

  ADD_HEALTH_STATE: async (context, payload) => {
    const token = context.rootState.token;
    const { data } = payload;

    await clinicApi.addHealthState(token, data);

    await context.dispatch('LOAD_HEALTH_STATE', data.user);
  },

  LOAD_HEALTH_CONTENT: async (context) => {
    const data = await clinicApi.getHealthContent(context.rootState.token);

    context.commit('SET_HEALTH_CONTENT', data.result);
  },

  LOAD_ANAMNESES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getAnamneses(token, payload);
    const data = { items: result.result, clientId: payload };

    context.commit('SET_ANAMNESES', data);
  },

  ADD_ANAMNESIS: async (context, payload) => {
    const token = context.rootState.token;
    const { user, text, date } = payload;

    await clinicApi.addAnamnesis(token, { user, text, date });

    await context.dispatch('LOAD_ANAMNESES', user);
  },

  UPDATE_ANAMNESIS: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user, text, date } = payload;

    await clinicApi.updateAnamnesis(token, id, { text, date });

    await context.dispatch('LOAD_ANAMNESES', user);
  },

  DELETE_ANAMNESIS: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user } = payload;

    await clinicApi.deleteAnamnesis(token, id);

    await context.dispatch('LOAD_ANAMNESES', user);
  },

  LOAD_STRATEGIES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getStrategies(token);
    const data = { items: result.result };

    context.commit('SET_STRATEGIES', data);
  },

  LOAD_STAGES: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getStages(token, payload);
    const data = { items: result.result, clientId: payload };

    context.commit('SET_STAGES', data);
  },

  ADD_STAGE: async (context, payload) => {
    const token = context.rootState.token;
    const { user, title, goals, dateFrom, dateTo } = payload;

    await clinicApi.addStage(token, { user, title, goals, dateFrom, dateTo });

    await context.dispatch('LOAD_STAGES', user);
  },

  UPDATE_STAGE: async (context, payload) => {
    const token = context.rootState.token;
    const { id, user, title, goals, dateFrom, dateTo } = payload;

    await clinicApi.updateStage(token, id, { title, goals, dateFrom, dateTo });

    await context.dispatch('LOAD_STAGES', user);
  },

  CHANGE_GOAL_STATUS: async (context, payload) => {
    const token = context.rootState.token;
    const { id, stage, status, user } = payload;

    await clinicApi.changeGoalStatus(token, id, stage, status);

    await context.dispatch('LOAD_STAGES', user);
  },

  CLINIC_LOAD_DRUG_PRESCRIPTIONS: async (context, payload) => {
    const token = context.rootState.token;
    const result = await clinicApi.getDrugPrescriptions(token, payload);
    const data = { items: result.result.items, clientId: payload };

    context.commit('CLINIC_SET_DRUG_PRESCRIPTIONS', data);
  },
};

export default {
  state,
  getters,
  mutations,
  actions,
};